import { useMutation } from '@apollo/client/react';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { Button } from '@optools/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { SYNC_PIPELINE_ROUTING } from '@optools/services/operator-gateway/logger/mutations';
import { MediaFeedType } from '@optools/services/operator-gateway/types/generated/graphql';
import { selectLoggerConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { capitalize } from 'es-toolkit';
import { toast } from 'sonner';

import { determineNotificationPosition, VerticalPosition } from '../utils/notification';

export interface SyncBroadcastsButtonProps {
  mediaId: string;
  side: MediaFeedType;
}

export function SyncBroadcastButton({ mediaId, side }: SyncBroadcastsButtonProps) {
  const { enableBroadcastSync } = useAppSelector(selectLoggerConfig);

  const [syncPipelineRouting, { loading }] = useMutation(SYNC_PIPELINE_ROUTING, {
    onCompleted: (data) => {
      const success = data?.syncPipelineRouting[0]?.success;
      const reason = data?.syncPipelineRouting[0]?.reason;
      if (!success) {
        toast.error('Sync Broadcast Error', {
          description: reason || `Failed to sync ${capitalize(side)} broadcast source`,
          duration: Infinity,
          position: determineNotificationPosition(side, VerticalPosition.BOTTOM),
        });
      } else {
        toast.success('Sync Broadcast Success', {
          description: `Synced broadcast source for ${capitalize(side)}`,
          position: determineNotificationPosition(side, VerticalPosition.BOTTOM),
        });
      }
    },
  });

  const handleSyncBroadcast = () => {
    syncPipelineRouting({ variables: { mediaIds: [mediaId] } });
  };

  if (!enableBroadcastSync) return undefined;

  return (
    <Tooltip>
      <ConfirmationDialog
        description={`Sync ${capitalize(side)} broadcast?`}
        title={`Sync Broadcast`}
        onConfirm={handleSyncBroadcast}>
        <TooltipTrigger asChild>
          <Button disabled={loading} loading={loading}>
            Sync Broadcast
          </Button>
        </TooltipTrigger>
      </ConfirmationDialog>
      <TooltipContent>{`Attempts to sync ${capitalize(side)} video feeds with source`}</TooltipContent>
    </Tooltip>
  );
}
